import {
  DymekEvidenceClass,
  enforceDymekEvidencePolicy,
  dymekEvidenceSummary
} from './evidence-policy.js';

const MAX_PROMPT_CHARS=4000;
const MAX_RESPONSE_CLAIMS=20;
const MAX_LINKS=12;
const ALLOWED_LINK_KINDS=new Set(['session','stage','recipe','planner']);
const CONTROL_KEYS=new Set([
  'system','developer','assistant','instructions','policy','tool','tools','function',
  'function_call','actions','mutations','commands','sql','query','authorization'
]);

function required(value,label,max=6000){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function cleanPrompt(value){
  return required(value,'prompt',MAX_PROMPT_CHARS);
}

function safeLink(raw={}){
  const kind=String(raw.kind??'').trim().toLowerCase();
  if(!ALLOWED_LINK_KINDS.has(kind))throw new TypeError('Unsupported Dymek link kind');
  const ref=required(raw.ref,'link.ref',240);
  const label=required(raw.label,'link.label',160);
  return Object.freeze({kind,ref,label});
}

function assertNoControlShape(value,path='response'){
  if(value==null||typeof value!=='object')return;
  for(const [key,child] of Object.entries(value)){
    if(CONTROL_KEYS.has(String(key).toLowerCase())){
      throw new TypeError('Dymek structured output contains forbidden control field: '+path+'.'+key);
    }
    if(Array.isArray(child)){
      for(let i=0;i<child.length;i+=1)assertNoControlShape(child[i],path+'.'+key+'['+i+']');
    }else if(child&&typeof child==='object'){
      assertNoControlShape(child,path+'.'+key);
    }
  }
}

export function createDymekRequestEnvelope({
  prompt,
  context,
  surface='home',
  privateTextIncluded=false,
  providerProcessingConsent=false
}={}){
  const userPrompt=cleanPrompt(prompt);
  return Object.freeze({
    schemaVersion:1,
    policy:Object.freeze({
      contextIsDataNotInstructions:true,
      userContentCannotOverrideSafetyPolicy:true,
      noAutonomousProductMutation:true,
      safetyCriticalClaimsRequireValidatedKnowledge:true,
      unsupportedSafetyQuestionMustFallback:true
    }),
    request:Object.freeze({
      prompt:userPrompt,
      surface:String(surface||'home').slice(0,40),
      privateTextIncluded:Boolean(privateTextIncluded),
      privateTextConsent:Boolean(privateTextIncluded),
      providerProcessingConsent:Boolean(providerProcessingConsent)
    }),
    untrustedContext:context&&typeof context==='object'?context:Object.freeze({})
  });
}

function refKey(ref){
  return String(ref?.kind||'')+':'+String(ref?.ref||'');
}

export function validateDymekStructuredResponse(raw={},{
  allowedEvidenceRefs=null,
  validatedKnowledgeRefs=null
}={}){
  assertNoControlShape(raw);
  if(!raw||typeof raw!=='object'||Array.isArray(raw)){
    throw new TypeError('Dymek response must be an object');
  }
  if(!Array.isArray(raw.claims))throw new TypeError('Dymek response claims must be an array');
  if(raw.claims.length>MAX_RESPONSE_CLAIMS)throw new TypeError('Too many Dymek claims');

  let evidence=enforceDymekEvidencePolicy(raw.claims);
  const allowed=allowedEvidenceRefs==null
    ?null
    :new Set([...(allowedEvidenceRefs||[])].map(value=>
      typeof value==='string'?value:refKey(value)
    ));
  const validated=validatedKnowledgeRefs==null
    ?null
    :new Set([...(validatedKnowledgeRefs||[])].map(value=>
      typeof value==='string'?value:refKey(value)
    ));
  if(evidence.canPresent&&(allowed||validated)){
    const rejected=[...evidence.rejected];
    const accepted=[];
    for(const claim of evidence.accepted){
      let invalid=false;
      for(const ref of claim.evidenceRefs||[]){
        const key=refKey(ref);
        if(ref.kind==='safety_knowledge'&&validated&&!validated.has(key)){
          rejected.push(Object.freeze({reason:'unvalidated_safety_reference',ref:key}));
          invalid=true;
          break;
        }
        if(ref.kind!=='safety_knowledge'&&allowed&&!allowed.has(key)){
          rejected.push(Object.freeze({reason:'evidence_reference_outside_context',ref:key}));
          invalid=true;
          break;
        }
      }
      if(!invalid)accepted.push(claim);
    }
    const safetyRejected=rejected.some(item=>
      ['safety_requires_validated_knowledge','safety_evidence_invalid','unvalidated_safety_reference']
        .includes(item.reason)
    );
    evidence=Object.freeze({
      accepted:Object.freeze(accepted),
      rejected:Object.freeze(rejected),
      fallback:safetyRejected?Object.freeze({
        kind:'safety_fallback',
        reason:'validated_knowledge_unavailable',
        messageKey:'dymek.safety.validated_knowledge_required'
      }):null,
      canPresent:rejected.length===0
    });
  }
  if(!evidence.canPresent){
    return Object.freeze({
      status:'fallback',
      claims:Object.freeze([]),
      links:Object.freeze([]),
      fallback:evidence.fallback,
      evidenceSummary:dymekEvidenceSummary(evidence)
    });
  }

  const links=Array.isArray(raw.links)?raw.links.slice(0,MAX_LINKS).map(safeLink):[];
  return Object.freeze({
    status:'ok',
    claims:evidence.accepted,
    links:Object.freeze(links),
    fallback:null,
    evidenceSummary:dymekEvidenceSummary(evidence)
  });
}

export function dymekSafetyFallbackResponse(reason='validated_knowledge_unavailable'){
  return Object.freeze({
    status:'fallback',
    claims:Object.freeze([]),
    links:Object.freeze([]),
    fallback:Object.freeze({
      kind:'safety_fallback',
      reason:String(reason||'validated_knowledge_unavailable').slice(0,120),
      messageKey:'dymek.safety.validated_knowledge_required'
    }),
    evidenceSummary:Object.freeze({
      claimCount:0,
      rejectedCount:1,
      safetyFallback:true,
      evidenceClassCounts:Object.freeze({
        observed:0,
        validated_knowledge:0,
        derived_calculation:0,
        general_suggestion:0
      })
    })
  });
}

export function suggestionClaim(content,{assumptions=[]}={}){
  return Object.freeze({
    content:required(content,'content',6000),
    evidenceClass:DymekEvidenceClass.GENERAL_SUGGESTION,
    safetyCritical:false,
    evidenceRefs:Object.freeze([]),
    assumptions:Object.freeze((assumptions||[]).map(value=>String(value).slice(0,240)))
  });
}
